import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { calcSimulation, saveSimulationInfo } from '@store/actions/planAction';
import SatSelect from '@components/common/satUI/SatSelect';
import SatInput from '@components/common/satUI/SatInput';
import { GridHeader } from '@components/common/grid';
import { showAlert } from '@store/actions/commonAction';
import { getRnBnf } from '@common/utils/simulation';

const SilverNursingCareRider = forwardRef((props, ref) => {
  const {
    simulationInfo: {
      simulationList = [],
      verifyPayload,
      productInfo,
      silverNursingCareRiderInfo = {},
      calculationContent = {},
    },
    type,
  } = props;

  const dispatch = useDispatch();
  const [currencyCode, setCurrencyCode] = useState('');

  useEffect(() => {
    setCurrencyCode(verifyPayload?.currencyCode);
    if (!silverNursingCareRiderInfo.requestAge) {
      changeSimulationCondition(
        'requestAge',
        getStepOptions('requestAge')[0].value,
      );
   }
  }, [silverNursingCareRiderInfo]);

  useImperativeHandle(ref, () => ({
    calc: () => handleCalcClick(),
    checkValid: () => true,
  }));

  const handleCalcClick = () => {
    if (!checkValid()) {
      return;
    }
    const payload = {
      simulType: 'silverNursingRider',
      simlNm: type,
      calcBasDd: verifyPayload.calculateDate,
      prodCd: productInfo?.productDetail?.productCode,
      sncRqstAge: silverNursingCareRiderInfo.requestAge,
      gndrCd: verifyPayload.genderCode,
      faceAmnt: silverNursingCareRiderInfo.faceAmount,
      sncPayRtCd: parseInt(silverNursingCareRiderInfo.payRate * 100),
      insrnAge: verifyPayload.insuranceAge,
    };
    dispatch(calcSimulation(payload));
  };

  const checkValid = () => {
    const faceAmount = +String(silverNursingCareRiderInfo.faceAmount).replace(
      /,/g,
      '',
    );
    // 주계약 가입금액 validation
    if (currencyCode !== '840') {
      if (faceAmount < 10_000_000) {
        dispatch(
          showAlert({ message: '주계약 가입금액 최저한도는 1,000만원입니다.' }),
        );
        return false;
      }
    }

    // 장기 간병선지급금 신청 비율 validation
    if (silverNursingCareRiderInfo.payRate < 0.1 && faceAmount < 500_000_000) {
      dispatch(
        showAlert({
          message:
            '주계약 가입금액 5억원 이상일 경우에만 장기간병 선지급금 신청비율을 1%-10%로 선택할 수 있습니다.',
        }),
      );
      return false;
    }
    return true;
  };

  const changeSimulationCondition = (type, value) => {
    let resetObj = {};

    dispatch(
      saveSimulationInfo({
        silverNursingCareRiderInfo: {
          ...resetObj,
          [type]: value,
        },
      }),
    );

    if (type === 'requestAge') {
      const planEstablishProductCode = (productInfo?.productDetail?.productCode.substring(0,6)) || '';
      if (('407201, 407301').includes(planEstablishProductCode)) changeSimulationCondition('faceAmount', getRnBnf(value, calculationContent));    
    }
  };

  /**
   *
   * @param {*} type     선택항목 타입
   * @returns
   */
  const getStepOptions = type => {
    let options = [];
    let age = 0;
    let remain = 0;
    let resultAge = 0;
    const { payinPeriodTypeCode, payinPeriodValue } =
      verifyPayload.planEstablishProduct[0];
    const { insuranceAge } = verifyPayload;

    switch (type) {
      case 'payRate':
        for (let i = 1; i < 11; i++) {
          options.push({
            label: `${i}%`,
            value: i / 100,
          });
        }
        break;
      case 'requestAge':
        if (payinPeriodTypeCode === '1') {
          // 년납
          age = insuranceAge + +payinPeriodValue;
        } else {
          // 세납
          age = +payinPeriodValue;
        }
        remain = age % 5 ? 5 - (age % 5) : 0;
        resultAge = Math.max(age + remain, 60);
        for (let i = 0; i < 5; i++) {
          options.push({
            label: `${resultAge}세`,
            value: resultAge,
          });
          resultAge += 5;
          if (resultAge > 95) {
            break;
          }
        }
        break;
    }
    return options;
  };

  const SearchOption = props => {
    const {
      silverNursingCareRiderInfo: { faceAmount, payRate, requestAge, isUSD },
    } = props;
    return (
      <>
        <table className="tbl-sty6">
          <colgroup>
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col />
          </colgroup>
          <tbody>
            <tr>
              <td>
                <span className="tit-small">주계약가입금액</span>
              </td>
              <td>
                <div className="amount w-154">
                  <SatInput
                    type={'currency'}
                    currencyType={isUSD ? 'D' : 'W'}
                    value={faceAmount}
                    displayValue={$utils.commaNumValue(faceAmount)}
                    dataIndex={'premium'}
                    useRecommendBtn={'premium'}
                    outerSuffix={() => (
                      <span className="txt">{isUSD ? '달러' : '원'}</span>
                    )}
                    onChange={value =>
                      changeSimulationCondition('faceAmount', value)
                    }
                  />
                </div>
              </td>
              <td>
                <span className="tit-small">장기간병선지급금 신청비율</span>
              </td>
              <td>
                <SatSelect
                  value={payRate}
                  options={getStepOptions('payRate')}
                  customStyleClass="w-100"
                  onChange={value =>
                    changeSimulationCondition('payRate', value.value)
                  }
                />
                비교 비율: 15%, 20%
              </td>
            </tr>
            <tr>
              <td>
                <span className="tit-small">최초신청나이</span>
              </td>
              <td>
                <SatSelect
                  value={requestAge}
                  options={getStepOptions('requestAge')}
                  customStyleClass="w-100"
                  onChange={value =>
                    changeSimulationCondition('requestAge', value.value)
                  }
                />
              </td>
              <td colSpan="2"></td>
            </tr>
          </tbody>
        </table>
      </>
    );
  };

  const SilverNursingCareRiderGrid = props => {
    const { calcData } = props;
    if (!calcData?.silverNursingCareInfo) {
      return <></>;
    }

    const data = calcData.silverNursingCareInfo;
    const sncUpfrntRqstRate1 = data[0].upfrontamntlist[0].sncUpfrntRqstRate;
    const sncUpfrntRqstRate2 = data[0].upfrontamntlist[1].sncUpfrntRqstRate;
    const sncUpfrntRqstRate3 = data[0].upfrontamntlist[2].sncUpfrntRqstRate;

    return (
      <>
        <div className="modal-scroll-wrap2">
          <table className="tbl-sty5 text-center">
            <colgroup>
              <col />
              <col />
              <col />
              <col />
              <col />
              <col />
              <col />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th rowSpan="3">최초 장기간병 선지급금 신청나이</th>
                <th rowSpan="3">매회 장기간병선지급금 신청나이</th>
                <th colSpan="6">
                  주계약 사망보장금액 중 장기간병 선지급금 비율
                </th>
              </tr>
              <tr>
                <th colSpan="2">{sncUpfrntRqstRate1}%</th>
                <th colSpan="2">{sncUpfrntRqstRate2}%</th>
                <th colSpan="2">{sncUpfrntRqstRate3}%</th>
              </tr>
              <tr>
                <th>선지급대상 보험금액</th>
                <th>장기간병 선지급금 지급 후 주계약 사망보장금액</th>
                <th>선지급대상 보험금액</th>
                <th>장기간병 선지급금 지급 후 주계약 사망보장금액</th>
                <th>선지급대상 보험금액</th>
                <th>장기간병 선지급금 지급 후 주계약 사망보장금액</th>
              </tr>
            </thead>
            <tbody>
              {data.map(m => {
                if (m.yearlycareinfo && m.yearlycareinfo.length > 0) {
                  return (
                    <>
                      {m.yearlycareinfo.map((careInfo, idx) => {
                        if (idx === 0) {
                          return (
                            <tr key={idx}>
                              <td rowSpan="7">{m.sncFstRqstAge}</td>
                              <td>{careInfo.sncRqstAge}</td>
                              <td>
                                {careInfo.upfrntAmnt1
                                  ? $utils.formatComma(careInfo.upfrntAmnt1)
                                  : ''}
                              </td>
                              <td>
                                {careInfo.suminsured1
                                  ? $utils.formatComma(careInfo.suminsured1)
                                  : ''}
                              </td>
                              <td>
                                {careInfo.upfrntAmnt2
                                  ? $utils.formatComma(careInfo.upfrntAmnt2)
                                  : ''}
                              </td>
                              <td>
                                {careInfo.suminsured2
                                  ? $utils.formatComma(careInfo.suminsured2)
                                  : ''}
                              </td>
                              <td>
                                {careInfo.upfrntAmnt3
                                  ? $utils.formatComma(careInfo.upfrntAmnt3)
                                  : ''}
                              </td>
                              <td>
                                {careInfo.suminsured3
                                  ? $utils.formatComma(careInfo.suminsured3)
                                  : ''}
                              </td>
                            </tr>
                          );
                        } else {
                          return (
                            <tr key={idx}>
                              <td>{careInfo.sncRqstAge}</td>
                              <td>
                                {careInfo.upfrntAmnt1
                                  ? $utils.formatComma(careInfo.upfrntAmnt1)
                                  : ''}
                              </td>
                              <td>
                                {careInfo.suminsured1
                                  ? $utils.formatComma(careInfo.suminsured1)
                                  : ''}
                              </td>
                              <td>
                                {careInfo.upfrntAmnt2
                                  ? $utils.formatComma(careInfo.upfrntAmnt2)
                                  : ''}
                              </td>
                              <td>
                                {careInfo.suminsured2
                                  ? $utils.formatComma(careInfo.suminsured2)
                                  : ''}
                              </td>
                              <td>
                                {careInfo.upfrntAmnt3
                                  ? $utils.formatComma(careInfo.upfrntAmnt3)
                                  : ''}
                              </td>
                              <td>
                                {careInfo.suminsured3
                                  ? $utils.formatComma(careInfo.suminsured3)
                                  : ''}
                              </td>
                            </tr>
                          );
                        }
                      })}
                      {m.upfrontamntlist && m.upfrontamntlist.length > 0 && (
                        <>
                          <tr style={{ backgroundColor: '#fffbeb' }}>
                            <td colSpan="2">장기간병 선지급금액(가능횟수)</td>
                            <td colSpan="2">
                              {$utils.formatComma(
                                m.upfrontamntlist[0].sncUpfrntAmnt,
                              )}
                              ({m.upfrontamntlist[0].sncUpfrntPsblNt}회)
                            </td>
                            <td colSpan="2">
                              {$utils.formatComma(
                                m.upfrontamntlist[1].sncUpfrntAmnt,
                              )}
                              ({m.upfrontamntlist[1].sncUpfrntPsblNt}회)
                            </td>
                            <td colSpan="2">
                              {$utils.formatComma(
                                m.upfrontamntlist[2].sncUpfrntAmnt,
                              )}
                              ({m.upfrontamntlist[2].sncUpfrntPsblNt}회)
                            </td>
                          </tr>
                          <tr style={{ backgroundColor: '#fffbeb' }}>
                            <td colSpan="2">
                              장기간병 선지급금액 최종지급 후 주계약가입금액
                            </td>
                            <td colSpan="2">
                              {$utils.formatComma(
                                m.upfrontamntlist[0].sncFnlPayAfFaceamnt,
                              )}
                            </td>
                            <td colSpan="2">
                              {$utils.formatComma(
                                m.upfrontamntlist[1].sncFnlPayAfFaceamnt,
                              )}
                            </td>
                            <td colSpan="2">
                              {$utils.formatComma(
                                m.upfrontamntlist[2].sncFnlPayAfFaceamnt,
                              )}
                            </td>
                          </tr>
                        </>
                      )}
                    </>
                  );
                } else {
                  return <></>;
                }
              })}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  return (
    <>
      <SearchOption silverNursingCareRiderInfo={silverNursingCareRiderInfo} />
      <div
        style={{
          display: 'flex',
          justifyContent: 'end',
          padding: '10px',
        }}
      >
        {currencyCode === '840' ? '(단위: 달러)' : '(단위: 원)'}
      </div>
      <GridHeader title="나이별 장기간병선지급금 예시" />
      <SilverNursingCareRiderGrid
        calcData={simulationList.find(item => item.simlNm === type)?.calcData}
      />
    </>
  );
});

export default React.memo(SilverNursingCareRider);
